import { useQuery } from 'react-query';
import feathersClient from '../../../apiClient';
import { getPaginatedResponse } from '../../../apiClient/utils';
import { TyTable, TyTableCell, TyTableRow } from '@tymbe/ty-components/table';
import { TableHeadCellSortable } from '../../../components/Table/TableHeadCellSortable';
import { TableHead, TablePagination, useTablePagination } from '../../../components/Table';
import useTableSort from '../../../components/Table/useTableSort';
import { ContractDocumentTypes } from '@tymbe/schema/enums';
import Spinner from '../../../components/Spinner';
import PersonName from '../../../components/dataEntity/PersonName';
import CompanyName from '../../../components/dataEntity/CompanyName';
import moment from 'moment';

interface JmhzContractsPageProps {
  query?: object;
}

const JmhzContractsPage = ({ query }: JmhzContractsPageProps) => {
  const { addSort, sortString: sort } = useTableSort({
    initialSortField: 'ppv.id',
    initialSort: -1,
  });
  const { setPageParams, pageSize, pageStart } = useTablePagination();
  const [field, sortOrder] = sort?.split('=') ?? ['ppv.id', 1];

  const { data, isLoading } = useQuery({
    queryKey: [
      'jmhz',
      'contracts',
      {
        ...query,
        $sort: Number(sortOrder) ? { [field]: Number(sortOrder) } : undefined,
        $limit: pageSize,
        $skip: pageStart,
      },
    ] as const,
    queryFn: async ({ queryKey: [, , q] }) => {
      const result = await feathersClient.service('person-document').find({
        query: {
          $leftJoinRelation: '[documentType, ppv]',
          $eager: '[person, company, documentType, ppv]',
          'documentType.type': { $in: Object.values(ContractDocumentTypes) },
          ...q,
          $sort: {
            ...q.$sort,
          },
        },
      });
      return getPaginatedResponse(result);
    },
  });

  return (
    <>
      <TyTable className="w-full">
        <TableHead>
          <TyTableRow>
            <TableHeadCellSortable {...addSort('person_id')}>Tymber ID</TableHeadCellSortable>
            <TableHeadCellSortable {...addSort('person.last_name')}>Tymber</TableHeadCellSortable>
            <TableHeadCellSortable {...addSort('company_id')}>Firma</TableHeadCellSortable>
            <TableHeadCellSortable {...addSort('documentType.type')}>Typ smlouvy</TableHeadCellSortable>
            <TableHeadCellSortable {...addSort('valid_from')}>Platná od</TableHeadCellSortable>
            <TableHeadCellSortable {...addSort('valid_to')}>Platná do</TableHeadCellSortable>
          </TyTableRow>
        </TableHead>
        <tbody>
          {isLoading && (
            <TyTableRow>
              <TyTableCell colSpan={6} align="center">
                <Spinner show={isLoading} className="w-6" />
              </TyTableCell>
            </TyTableRow>
          )}
          {data?.data.length === 0 ? (
            <TyTableRow>
              <TyTableCell colSpan={6} align="center">
                Žádná data
              </TyTableCell>
            </TyTableRow>
          ) : null}

          {data?.data.map((item) => (
            <TyTableRow key={item.id}>
              <TyTableCell>{item.person_id}</TyTableCell>
              <TyTableCell>
                <PersonName person_id={item.person_id} link />
              </TyTableCell>
              <TyTableCell>
                <CompanyName company_id={item.company_id ?? 0} link />
              </TyTableCell>
              <TyTableCell>{item.documentType?.type}</TyTableCell>
              <TyTableCell>{moment(item.valid_from).format('DD.MM.YYYY')}</TyTableCell>
              <TyTableCell>{moment(item.valid_to).format('DD.MM.YYYY')}</TyTableCell>
            </TyTableRow>
          ))}
        </tbody>
      </TyTable>
      <TablePagination {...setPageParams()} rowsCount={data?.total} />
    </>
  );
}

export default JmhzContractsPage;
